import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  FormHelperText,
  InputAdornment,
} from '@mui/material';
import {
  People as PeopleIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
  Hotel as BedroomIcon,
} from '@mui/icons-material';
import { ListingForm as ListingFormType } from '@/types';

interface CapacityTabProps {
  formData: ListingFormType;
  errors: Record<string, string>;
  onFormDataChange: (updates: Partial<ListingFormType>) => void;
  onErrorsChange: (errors: Record<string, string>) => void;
  clearErrors: (fields: string[]) => void;
}

export const CapacityTab: React.FC<CapacityTabProps> = ({
  formData,
  errors,
  onFormDataChange,
  clearErrors,
}) => {
  const handleInputChange = (field: string, value: number) => {
    onFormDataChange({ [field]: value });
    clearErrors([field]);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Capacity & Layout
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tell guests about your property's capacity and sleeping arrangements.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Guest Capacity
          </Typography>
          <FormHelperText sx={{ mb: 2 }}>
            How many guests can your property accommodate?
          </FormHelperText>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            type="number"
            label="Maximum Guests"
            value={formData.maxGuests}
            onChange={(e) => handleInputChange('maxGuests', parseInt(e.target.value) || 1)}
            error={!!errors.maxGuests}
            helperText={errors.maxGuests || 'Total number of guests'}
            inputProps={{ min: 1, max: 50 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PeopleIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
            Rooms & Beds
          </Typography>
          <FormHelperText sx={{ mb: 2 }}>
            Provide details about bedrooms, bathrooms, and sleeping arrangements.
          </FormHelperText>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            type="number"
            label="Bedrooms"
            value={formData.bedrooms}
            onChange={(e) => handleInputChange('bedrooms', parseInt(e.target.value) || 1)}
            error={!!errors.bedrooms}
            helperText={errors.bedrooms || 'Number of bedrooms'}
            inputProps={{ min: 0, max: 20 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BedroomIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            type="number"
            label="Bathrooms"
            value={formData.bathrooms}
            onChange={(e) => handleInputChange('bathrooms', parseInt(e.target.value) || 1)}
            error={!!errors.bathrooms}
            helperText={errors.bathrooms || 'Number of bathrooms'}
            inputProps={{ min: 0.5, max: 20, step: 0.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BathtubIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            type="number"
            label="Beds"
            value={formData.beds || 1}
            onChange={(e) => handleInputChange('beds', parseInt(e.target.value) || 1)}
            error={!!errors.beds}
            helperText={errors.beds || 'Total number of beds'}
            inputProps={{ min: 0, max: 50 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BedIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Tip:</strong> Be accurate with your capacity information. Guests rely on this to determine if your property meets their needs. Consider all sleeping arrangements including sofa beds, air mattresses, and pull-out couches.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CapacityTab;